import React from 'react';
/**
 * useGanttDatePicker Hook - Manages date picker state and navigation
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { addMonths, subMonths } from 'date-fns';
import { getTodayInBrasilia } from '../lib/utils';

interface UseGanttDatePickerReturn {
  selectedDate: Date;
  showDatePicker: boolean;
  setShowDatePicker: (show: boolean) => void;
  pickerMonth: Date;
  datePickerRef: React.RefObject<HTMLDivElement>;
  handleDateSelect: (date: Date) => void;
  handlePrevMonth: () => void;
  handleNextMonth: () => void;
  handleToday: () => void;
  handlePreviousDay: () => void;
  handleNextDay: () => void;
  dateParam: string | null;
}

export function useGanttDatePicker(): UseGanttDatePickerReturn {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('date');

  const selectedDate = useMemo(() => {
    if (dateParam) {
      const [year, month, day] = dateParam.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return getTodayInBrasilia();
  }, [dateParam]);

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(new Date());
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Initialize picker month when selectedDate changes
  useEffect(() => {
    setPickerMonth(selectedDate);
  }, [selectedDate]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDatePicker]);

  const handleDateSelect = useCallback((date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    const newParams = new URLSearchParams(searchParams);
    newParams.set('date', dateStr);
    navigate(`/cloud/gantt-chart?${newParams.toString()}`);
    setShowDatePicker(false);
  }, [navigate, searchParams]);

  const handlePrevMonth = useCallback(() => setPickerMonth(prev => subMonths(prev, 1)), []);
  const handleNextMonth = useCallback(() => setPickerMonth(prev => addMonths(prev, 1)), []);
  const handleToday = useCallback(() => handleDateSelect(new Date()), [handleDateSelect]);

  const handlePreviousDay = useCallback(() => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    handleDateSelect(newDate);
  }, [selectedDate, handleDateSelect]);

  const handleNextDay = useCallback(() => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    handleDateSelect(newDate);
  }, [selectedDate, handleDateSelect]);

  return {
    selectedDate,
    showDatePicker,
    setShowDatePicker,
    pickerMonth,
    datePickerRef,
    handleDateSelect,
    handlePrevMonth,
    handleNextMonth,
    handleToday,
    handlePreviousDay,
    handleNextDay,
    dateParam,
  };
}
